<script lang="ts">
  import { api } from "$lib/api/client";
  import { toasts } from "$lib/stores/toasts.svelte";
  import { fmtBRL } from "$lib/utils/format";

  let { onConfirmed = () => {} }: { onConfirmed?: () => void } = $props();

  // ── Estado ────────────────────────────────────────────────────
  let pending   = $state<any[]>([]);
  let loaded    = $state(false);
  let expanded  = $state(false);

  // Tx sendo confirmada no modal
  let confirmingTx  = $state<any | null>(null);
  let isRecurring   = $state(false);
  let recFrequency  = $state<"daily" | "weekly" | "monthly" | "yearly">("monthly");
  let recInterval   = $state(1);
  let recEndAt      = $state("");
  let saving        = $state(false);

  // ── Carga ─────────────────────────────────────────────────────
  async function load() {
    try {
      pending = await api<any[]>("/telegram/confirmations");
    } catch {
      // silencioso: usuário pode não ter Telegram configurado
    } finally {
      loaded = true;
    }
  }

  $effect(() => { load(); });

  // ── Ações ─────────────────────────────────────────────────────
  async function discard(tx: any) {
    if (!confirm(`Descartar "${tx.description}"?`)) return;
    try {
      await api(`/telegram/confirmations/${tx.id}`, { method: "DELETE" });
      pending = pending.filter((t) => t.id !== tx.id);
      toasts.success("Lançamento descartado");
    } catch (e: any) {
      toasts.error("Erro ao descartar", e.message);
    }
  }

  function openConfirm(tx: any) {
    confirmingTx = tx;
    isRecurring  = false;
    recFrequency = "monthly";
    recInterval  = 1;
    recEndAt     = "";
  }

  async function saveConfirm() {
    if (!confirmingTx) return;
    saving = true;
    try {
      await api(`/telegram/confirmations/${confirmingTx.id}/confirm`, {
        method: "POST",
        body: JSON.stringify({
          is_recurring: isRecurring,
          frequency:    isRecurring ? recFrequency : null,
          interval:     recInterval,
          end_at:       recEndAt ? new Date(recEndAt).toISOString() : null,
        }),
      });
      pending = pending.filter((t) => t.id !== confirmingTx!.id);
      confirmingTx = null;
      toasts.success("Lançamento confirmado!");
      onConfirmed();
    } catch (e: any) {
      toasts.error("Erro ao confirmar", e.message);
    } finally {
      saving = false;
    }
  }
</script>

{#if loaded && pending.length > 0}
  <!-- Banner amarelo compacto com contagem -->
  <div class="mx-auto max-w-7xl px-4 pt-3">
    <div class="rounded-xl border border-yellow-500/40 bg-yellow-500/10 text-yellow-200 px-4 py-3 flex items-center justify-between gap-3">
      <div class="flex items-center gap-2 text-sm font-medium">
        <span class="text-base">⚡</span>
        <span>
          {pending.length} lançamento{pending.length > 1 ? "s" : ""} via Telegram aguardando confirmação
        </span>
      </div>
      <button
        class="text-xs underline underline-offset-2 hover:text-yellow-100 transition-colors"
        onclick={() => (expanded = !expanded)}
      >
        {expanded ? "Fechar" : "Revisar"}
      </button>
    </div>

    {#if expanded}
      <div class="mt-2 space-y-2">
        {#each pending as tx (tx.id)}
          <div class="card flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-l-4 border-yellow-500/60">
            <div class="flex-1 min-w-0">
              <p class="font-semibold truncate">{tx.description}</p>
              <p class="text-xs text-muted">
                {tx.kind === "expense" ? "💸 Despesa" : "💰 Receita"} ·
                <span class="font-medium {tx.kind === 'expense' ? 'text-red-400' : 'text-emerald-400'}">
                  {fmtBRL(Number(tx.amount))}
                </span>
                · {tx.date}
              </p>
              {#if tx.notes}
                <p class="text-xs text-muted/70 truncate mt-0.5">{tx.notes}</p>
              {/if}
            </div>
            <div class="flex gap-2 shrink-0">
              <button
                class="btn-primary text-xs px-3 py-1.5"
                onclick={() => openConfirm(tx)}
              >
                ✅ Confirmar
              </button>
              <button
                class="btn-ghost text-xs px-3 py-1.5 text-red-400 hover:text-red-300"
                onclick={() => discard(tx)}
              >
                ✕ Descartar
              </button>
            </div>
          </div>
        {/each}
      </div>
    {/if}
  </div>
{/if}

<!-- Modal de confirmação + recorrência -->
{#if confirmingTx}
  <div
    class="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
    role="dialog" aria-modal="true"
    onkeydown={(e) => { if (e.key === "Escape") confirmingTx = null; }}
  >
    <div class="bg-surface rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-4">
      <h2 class="text-lg font-bold">Confirmar lançamento</h2>

      <div class="card space-y-1 bg-surface/60">
        <p class="font-semibold">{confirmingTx.description}</p>
        <p class="text-sm text-muted">
          {confirmingTx.kind === "expense" ? "💸 Despesa" : "💰 Receita"} ·
          <span class="font-medium {confirmingTx.kind === 'expense' ? 'text-red-400' : 'text-emerald-400'}">
            {fmtBRL(Number(confirmingTx.amount))}
          </span>
          · {confirmingTx.date}
        </p>
      </div>

      <!-- Opção de recorrência -->
      <label class="flex items-center gap-2 cursor-pointer select-none">
        <input type="checkbox" class="w-4 h-4 accent-yellow-400" bind:checked={isRecurring} />
        <span class="text-sm">Este gasto se repete (criar recorrência)</span>
      </label>

      {#if isRecurring}
        <div class="grid grid-cols-2 gap-3">
          <div>
            <label class="label text-xs" for="tg-freq">Frequência</label>
            <select id="tg-freq" class="input text-sm" bind:value={recFrequency}>
              <option value="daily">Diária</option>
              <option value="weekly">Semanal</option>
              <option value="monthly">Mensal</option>
              <option value="yearly">Anual</option>
            </select>
          </div>
          <div>
            <label class="label text-xs" for="tg-int">A cada</label>
            <input id="tg-int" type="number" min="1" max="60" class="input text-sm" bind:value={recInterval} />
          </div>
          <div class="col-span-2">
            <label class="label text-xs" for="tg-end">Termina em (opcional)</label>
            <input id="tg-end" type="date" class="input text-sm" bind:value={recEndAt} />
          </div>
        </div>
      {/if}

      <div class="flex gap-3 pt-2">
        <button class="btn-primary flex-1" disabled={saving} onclick={saveConfirm}>
          {saving ? "Salvando…" : "✅ Confirmar"}
        </button>
        <button class="btn-ghost" onclick={() => (confirmingTx = null)}>Cancelar</button>
      </div>
    </div>
  </div>
{/if}
